import React from 'react';
import { Space, Tag } from 'antd';
import './formStyles.css';

/** Shown on fields the go-live checklist counts as required. */
export const GO_LIVE_REQUIRED = 'Required for go-live';

export const formItemStyle = { marginBottom: 20 };

export const sectionTitleStyle = {
  fontWeight: 600,
  marginBottom: 8,
  color: '#101112',
};

export const descriptionsLabelStyle = { fontWeight: 600 };

export const platformFormClassName = 'platform-form';

/** Shared props for Platform grouped sections (light gray surface, flat layout). */
export const sectionCardProps = {
  size: 'small',
  className: 'platform-card',
  styles: { body: { padding: '16px 20px' } },
};

/** Vertical gap between sibling grouped sections on a tab. */
export const sectionStackGap = 20;

/** Shared content width for client detail tabs (matches SMS / Overview). */
export const tabContentStyle = { width: '100%', maxWidth: 880 };

/**
 * One wording for every setup status badge, so sections no longer mix
 * "Missing", "Incomplete", and "Not set up" for the same state.
 */
export function StatusTag({ ready, readyLabel = 'Ready', pendingLabel = 'Not set up' }) {
  return (
    <Tag color={ready ? 'success' : 'warning'}>
      {ready ? readyLabel : pendingLabel}
    </Tag>
  );
}

/**
 * Operational badges for a client. Suspended replaces Active so a billing hold
 * never sits next to a green Active tag. Inactive stays visible beside Suspended,
 * because new invoices stop only when both are true. Catering is independent.
 */
export function StatusTags({ org }) {
  const suspended = Boolean(org?.is_suspended);
  const active = Boolean(org?.is_active);
  return (
    <Space size={4} wrap>
      {suspended && !active && (
        <Tag color="default" bordered={false}>Inactive</Tag>
      )}
      {suspended ? (
        <Tag color="error">Suspended</Tag>
      ) : (
        <Tag color={active ? 'success' : 'default'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      )}
      {org?.is_catering && <Tag>Catering</Tag>}
    </Space>
  );
}

/**
 * A field can be needed for go-live without being safe to enforce on submit:
 * the General tab also holds the Active and Suspended switches, and a blocking
 * rule there would stop someone from clearing a billing hold. Ant Design's
 * `required` prop draws the asterisk without adding validation, so both cases
 * carry the same marker.
 */
export const goLiveMarkProps = { required: true };

/**
 * Toggles read as a sentence, so the label belongs beside the switch instead of
 * above it like the text inputs in these vertical forms.
 */
export const switchFieldProps = {
  layout: 'horizontal',
  colon: false,
  valuePropName: 'checked',
};

export function optionalLabel(text) {
  return (
    <span>
      {text}
      <span style={{
        fontWeight: 400, fontSize: 12, marginLeft: 6, color: 'rgba(0, 0, 0, 0.45)',
      }}
      >
        (optional)
      </span>
    </span>
  );
}

/**
 * Form.Item tooltip props: bold setting label + body (string, tipLines, or tipList).
 */
export function formTooltip(label, content) {
  return {
    title: (
      <div className="platform-tooltip">
        <div className="platform-tooltip-title">{label}</div>
        {typeof content === 'string' || typeof content === 'number' ? (
          <p className="platform-tooltip-line">{content}</p>
        ) : content}
      </div>
    ),
    rootClassName: 'platform-form-tooltip',
    overlayClassName: 'platform-form-tooltip',
  };
}

/** One paragraph per distinct sentence / idea. */
export function tipLines(...lines) {
  return lines.filter(Boolean).map((line) => (
    <p key={String(line)} className="platform-tooltip-line">{line}</p>
  ));
}

/** Intro line (optional) plus unordered or numbered bullets. */
export function tipList(items, { intro, ordered = false } = {}) {
  const ListTag = ordered ? 'ol' : 'ul';
  return (
    <>
      {intro ? <p className="platform-tooltip-line">{intro}</p> : null}
      <ListTag className="platform-tooltip-list">
        {items.map((item) => (
          <li key={String(item)}>{item}</li>
        ))}
      </ListTag>
    </>
  );
}
